import React from "react";
import { Flex, Box } from "../../../../react-shared/flexbox";
import NotificationStore from "../../Store/notification-store";
import { observer } from "mobx-react";
import moment from "moment";
import { Grid, GridCell } from "../../../../react-shared/Grid/Grid";
import parse from "html-react-parser";
import format from "string-format";
import { NotificationRow, renderNotificationRows } from "./NotificationRows";

interface NotificationAction {
    textKey: string;
    kind: string;
    values: { [key: string]: string };
}

// The producer sends what an action IS, not where it points, so the route lives here rather than
// being baked into the notification. A kind that is not listed renders nothing — better a missing
// link than a broken one (IP-504).
const ACTION_ROUTES: { [kind: string]: (values: { [key: string]: string }) => string | null } = {
    // Served by the notifications proxy rather than /integrationplatform, because that controller
    // is role-gated for its whole class and a customer-scoped reader must now be able to open the
    // logs for their own customers (IP-505).
    IntegrationLogs: (values) =>
        values && values.correlationToken
            ? `/mashie/notifications/logs/download/${encodeURIComponent(values.correlationToken)}`
            : null,
};

interface NotificationProps {
    id: string;
    type: string;
    title: string;
    text: string;
    isRead: boolean;
    isCompleted: boolean;
    createdAt: string;
    createdBy: string;
    localizedTextValues: { [key: string]: string };
    // Optional per-line content from the Integration Platform (IP-504). Absent for every other
    // producer, including Mashie's own healthcare jobs, which keep the original rendering below.
    rows?: NotificationRow[];
    actions?: NotificationAction[];
}


// @ts-ignore
@observer
export class Notification extends React.Component<NotificationProps> {
    constructor(props: NotificationProps) {
    super(props);
  }

  // The language strings still contain {clientList}, {summary} and {logsLink} surrounded by <br/>
  // tags, but the producer no longer sends any of them — the Integration Platform replaced all
  // three with rows and actions. string-format resolves a missing placeholder to "", leaving the
  // <br/> tags stranded and a gap above the rows. Collapsing them here keeps the language strings
  // untouched, which is a translation concern and not something to edit from this side.
  //
  // An earlier version also blanked those placeholders defensively before formatting. That is now
  // the API's job, and only the API can do it correctly: it is the side that knows whether a
  // notification is organisation-wide, which is what makes leaving the markup in place unsafe
  // rather than merely redundant.
  collapseStrandedBreaks = (html: string) =>
    html.replace(/(?:\s*<br\s*\/?>\s*){2,}/gi, "<br/>").replace(/(?:\s*<br\s*\/?>\s*)+$/i, "");

  localize = (text: string) => {
    if (text === undefined) {
      return "";
    }

    const resolved = Mashie.Resources.GetString(text);

    if (resolved.includes("Missing string")) {
      return text;
    }

    const formatted = format(resolved, this.props.localizedTextValues);
    const hasStructuredContent =
      (this.props.rows && this.props.rows.length > 0) ||
      (this.props.actions && this.props.actions.length > 0);

    return hasStructuredContent ? this.collapseStrandedBreaks(formatted) : formatted;
  }

  renderActions = () => {
    const actions = this.props.actions;

    if (!actions || actions.length === 0) {
      return null;
    }

    const rendered = actions
      .map((action, index) => {
        const buildRoute = ACTION_ROUTES[action.kind];

        if (!buildRoute) {
          return null;
        }

        const href = buildRoute(action.values ?? {});

        if (!href) {
          return null;
        }

        const resolved = Mashie.Resources.GetString(action.textKey);
        const label = resolved.includes("Missing string") ? action.textKey : resolved;

        return (
          <a key={index} className="notification-action" href={href}>
            {label}
          </a>
        );
      })
      .filter((element) => element !== null);

    return rendered.length > 0 ? <div className="notification-actions">{rendered}</div> : null;
  }

  render() {
    const textClasses = [
      "notification-text"
    ].join(" ");
    const notificationBoxClasses = [
      "notification-box"
    ].join(" ");

    var createdDate = moment
      .utc(this.props.createdAt)
      .local()
      .format("YYYY-MM-DD HH:mm");

    const title = this.localize(this.props.title);
    const text = this.localize(this.props.text);
    
    return (
      <div
        key={this.props.id}
        onClick={() => {
        
          !this.props.isRead
            ? NotificationStore.setAsRead(this.props.id)
            : null
            }
        }
      >
        <Grid
          className={notificationBoxClasses}
          columnTemplate="30px 70% 20%"
          rowTemplate="20px auto"
        >
          <GridCell
            className="readState"
            rowStart="1"
            rowEnd="1"
            columnStart="1"
            columnEnd="1"
            placeSelf="center"
          >
            {!this.props.isRead ? (
              <i className="fa fa-circle" aria-hidden="true"></i>
            ) : null}
          </GridCell>
          <GridCell
            className="note-date"
            rowStart="1"
            rowEnd="1"
            columnStart="2"
            columnEnd="2"
          >
            {createdDate} (
            {!this.props.createdBy ? "System" : this.props.createdBy})
          </GridCell>
          <GridCell
            className="doneState"
            rowStart="1" 
            rowEnd="1"
            columnStart="3"
            columnEnd="3"
          >
            {/* Kept, and still writes every row this reader can see, so it now reads as "mark all
                of mine" next to the per-row boxes below. Note the two are not symmetrical: this one
                ticks informational rows as well, which have no checkbox of their own, so working
                through the actionable rows one by one leaves this unticked until the reader says the
                whole notification is dealt with. Deliberate — "Fixed" here answers "is everything I
                can see dealt with", and narrowing it to the actionable rows would silently redefine
                what a completed notification means for every existing document (IP-512). */}
            {this.props.type === "ACTION" ? (
              <Flex alignItems="center">
                <Box style={{ height: "100%" }}>
                  {Mashie.Resources.GetString("Notification_IsCompleted")}
                </Box>
                <input
                  title={Mashie.Resources.GetString("Notification_IsCompleted_Shared_With_Organization")}
                  style={{ alignSelf: "normal" }}
                  onChange={() =>
                    NotificationStore.setDone(
                      this.props.id,
                      !this.props.isCompleted
                    )
                  }
                  type="checkbox"
                  checked={this.props.isCompleted}
                />
              </Flex>
            ) : null}
          </GridCell>
          <GridCell rowStart="2" rowEnd="2" columnStart="2" columnEnd="4">
            <div title={title} className="notification-title"> 
              {title}
            </div>
            <div className={textClasses}>{parse(text)}</div>
            {renderNotificationRows(this.props.id, this.props.rows)}
            {this.renderActions()}
          </GridCell>
        </Grid>
        <div className="divider"></div>
      </div>
    );
  }
}
